import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { IoCloseSharp } from 'react-icons/io5';
import { appendOperationalCostsNodeToPipeline, editOperationalCostsNode, getGlobalParams } from '../../../api/api';
import { usePipeline } from '../../../context/PipelineContext';
import { validateNodeName } from '../../../name_validation';
import { notifyError } from '../../../errors/notifyError';
import { OP } from '../../../api/operations';
import './OperationalCostsConfigModal.css';

// Set the app element for accessibility
Modal.setAppElement('#root');

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    borderRadius: '0.375rem',
    padding: '1rem',
    maxWidth: '1200px',
    width: '92vw',
    maxHeight: '92vh',
    overflow: 'auto',
    position: 'relative',
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: 9999, // Higher z-index to ensure it appears on top
  }
};

const OperationalCostsConfigModal = ({ isOpen, onClose, onConfigured, isReconfiguration = false, initialData = {} }) => {
  const [nodeName, setNodeName] = useState(initialData.name || '');
  const [nodeNameError, setNodeNameError] = useState('');
  
  // Form state for all parameters
  const [noOfBopYear, setNoOfBopYear] = useState(initialData.no_of_bop_year || 0);
  const [windBopEscalationPerc, setWindBopEscalationPerc] = useState(initialData.wind_bop_escalation_perc || 0);
  const [windEscalationPerc, setWindEscalationPerc] = useState(initialData.wind_escalation_perc || 0);
  const [solarEscalationPerc, setSolarEscalationPerc] = useState(initialData.solar_escalation_perc || 0);
  const [discountRate, setDiscountRate] = useState(initialData.discount_rate || 0);
  const [annualMaintenancePerTurbineInLakhs, setAnnualMaintenancePerTurbineInLakhs] = useState(
    initialData.annual_maintenance_per_turbine_in_lakhs ?? ''
  );
  const [annualMaintenancePerTurbineInLakhsWithBop, setAnnualMaintenancePerTurbineInLakhsWithBop] = useState(initialData.annual_maintenance_per_turbine_in_lakhs_with_bop || 0);
  const [annualMaintenancePerSolarUnitInLakhs, setAnnualMaintenancePerSolarUnitInLakhs] = useState(initialData.annual_maintenance_per_solar_unit_in_lakhs || 0);
  const [insurancePercentOfAssetCost, setInsurancePercentOfAssetCost] = useState(initialData.insurance_percent_of_asset_cost || 0);
  const [operationalCharges, setOperationalCharges] = useState(initialData.operational_charges || 0);
  const [assetManagementFees, setAssetManagementFees] = useState(initialData.asset_management_fees || 0);
  const [annualLeaseChargePerAcreSolar, setAnnualLeaseChargePerAcreSolar] = useState(
    (initialData.annual_lease_charge_per_acre_solar ?? initialData.annual_lease_charge_per_acre) || 0
  );
  const [annualLeaseChargePerAcreWind, setAnnualLeaseChargePerAcreWind] = useState(
    (initialData.annual_lease_charge_per_acre_wind ?? initialData.annual_lease_charge_per_acre) || 0
  );
  const [annualLeaseEscalationPerc, setAnnualLeaseEscalationPerc] = useState(initialData.annual_lease_escalation_perc || 0);
  const [solarAcresPerMWp, setSolarAcresPerMWp] = useState(initialData.solar_acres_per_MWp || 0);
  const [windAcresPerWTG, setWindAcresPerWTG] = useState(initialData.wind_acres_per_WTG || 0);
  const [firstYearBenifits, setFirstYearBenifits] = useState(initialData.first_year_benifits !== undefined ? initialData.first_year_benifits : true);
  const [includeDepreciation, setIncludeDepreciation] = useState(initialData.include_depreciation !== undefined ? initialData.include_depreciation : true);
  const [revenueFromExcessBankedUnitsPerUnit, setRevenueFromExcessBankedUnitsPerUnit] = useState(initialData.revenue_from_excess_banked_units_per_unit || 0);
  const [totalLapsedUnitsForExcessRevenuePerc, setTotalLapsedUnitsForExcessRevenuePerc] = useState(initialData.total_lapsed_units_for_excess_revenue_perc || 0);
  const [transmissionChargesPerKWh, setTransmissionChargesPerKWh] = useState(initialData.transmission_charges_per_KWh || 0);
  const [wheelingCharges, setWheelingCharges] = useState(initialData.wheeling_charges || 0);
  const [electricityDutyPerUnit, setElectricityDutyPerUnit] = useState(initialData.electricity_duty_per_unit || 0); 
  const [bankingChargeRs, setBankingChargeRs] = useState(initialData.banking_charge_rs ?? 0);
  const [perKW, setPerKW] = useState(initialData.per_KW || false);
  const [perKWh, setPerKWh] = useState(initialData.per_KWh || false);
  const [transmissionChargesPerKwPerMonth, setTransmissionChargesPerKwPerMonth] = useState(initialData.transmission_charges_per_Kw_per_month || 0);
  const { session } = usePipeline(); // Use the context to get the session
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [loadingGlobal, setLoadingGlobal] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  // Load only fields that are managed in Setup Utility to keep hidden state in sync
  useEffect(() => {
    const loadGlobal = async () => {
      if (!isOpen) return;
      if (!session?.pipeline_name || session.pipeline_name === 'Unsaved_Pipeline') return;
      setLoadingGlobal(true);
      try {
        const gp = await getGlobalParams(session.pipeline_name);
        const toNum = (v) => (v === '' || v === undefined || v === null ? '' : Number(v));
        // Basic configuration
        setInsurancePercentOfAssetCost(toNum(gp?.insurance_percent_of_asset_cost));
        setDiscountRate(toNum(gp?.discount_rate));
        // Maintenance costs
        // Keep raw value (string/dict/number) for wind non-BOP so node_func can parse series/dict
        setAnnualMaintenancePerTurbineInLakhs(gp?.annual_maintenance_per_turbine_in_lakhs ?? '');
        setAnnualMaintenancePerTurbineInLakhsWithBop(toNum(gp?.annual_maintenance_per_turbine_in_lakhs_with_bop));
        setAnnualMaintenancePerSolarUnitInLakhs(toNum(gp?.annual_maintenance_per_solar_unit_in_lakhs));
        setNoOfBopYear(toNum(gp?.no_of_bop_year));
        // Prefer dedicated Wind BOP key; fall back to legacy escalation_perc for older pipelines
        setWindBopEscalationPerc(toNum(gp?.wind_bop_escalation_perc ?? gp?.escalation_perc));
        setWindEscalationPerc(toNum(gp?.wind_escalation_perc));
        setSolarEscalationPerc(toNum(gp?.solar_escalation_perc));
        // Land & revenue
        setAnnualLeaseChargePerAcreSolar(toNum(gp?.annual_lease_charge_per_acre_solar ?? gp?.annual_lease_charge_per_acre));
        setAnnualLeaseChargePerAcreWind(toNum(gp?.annual_lease_charge_per_acre_wind ?? gp?.annual_lease_charge_per_acre));
        setAnnualLeaseEscalationPerc(toNum(gp?.annual_lease_escalation_perc));
        setSolarAcresPerMWp(toNum(gp?.solar_acres_per_MWp));
        setWindAcresPerWTG(toNum(gp?.wind_acres_per_WTG));
        setRevenueFromExcessBankedUnitsPerUnit(toNum(gp?.revenue_from_excess_banked_units_per_unit));
        setTotalLapsedUnitsForExcessRevenuePerc(toNum(gp?.total_lapsed_units_for_excess_revenue_perc));
      } finally {
        setLoadingGlobal(false);
      }
    };
    loadGlobal();
  }, [isOpen, session?.pipeline_name]);
  
  // Determine if we're in edit mode (has a name AND isConfigured is true but not reconfiguring)
  const isEditing = !!initialData.name && initialData.isConfigured === true && !isReconfiguration;
  
  // Validate node name
  useEffect(() => {
    // Always validate the node name, even if it's empty
    const validation = validateNodeName(nodeName);
    setNodeNameError(validation.isValid ? '' : validation.errorMessage);
  }, [nodeName]);

  const handleConfigure = async () => {
    try {
      setSubmitError(null);
      // Validate node name using the validation function
      const nameValidation = validateNodeName(nodeName);
      if (!nameValidation.isValid) {
        setNodeNameError(nameValidation.errorMessage);
        return;
      }
      
      // Helper function to convert empty strings to 0
      const toNumber = (value) => value === '' ? 0 : Number(value);
      
      // Get the input node names and position from initialData
      const inputNodes = initialData.inputNodes || {};
      const position = initialData.position || { x: 0, y: 0 };
      
      // Set submitting state to show loading indicator
      setIsSubmitting(true);
      
              // Log the configuration mode
      console.log("Configuration mode:", {
        isEditing,
        isReconfiguration,
        name: initialData.name,
        isConfigured: initialData.isConfigured
      });
      
      // Make the API call if we have a valid pipeline name
      if (session.pipeline_name !== "Unsaved_Pipeline") {
        try {
              if (isEditing) {
            // We're editing an existing node
            console.log(`Calling API to edit operational costs node: ${initialData.name} in pipeline: ${session.pipeline_name}`);
            
            await editOperationalCostsNode(
              session.pipeline_name,
              initialData.name,  // Use the original name
              toNumber(noOfBopYear),
              toNumber(windBopEscalationPerc),
              toNumber(windEscalationPerc),
              toNumber(solarEscalationPerc),
              // Pass raw wind non-BOP maintenance so backend can accept dict / CSV
              annualMaintenancePerTurbineInLakhs,
              toNumber(annualMaintenancePerTurbineInLakhsWithBop),
              toNumber(annualMaintenancePerSolarUnitInLakhs),
              toNumber(insurancePercentOfAssetCost),
              toNumber(operationalCharges),
              toNumber(assetManagementFees),
              toNumber(annualLeaseChargePerAcreSolar),
              toNumber(annualLeaseChargePerAcreWind),
              toNumber(annualLeaseEscalationPerc),
              toNumber(solarAcresPerMWp),
              toNumber(windAcresPerWTG),
              firstYearBenifits,
              toNumber(revenueFromExcessBankedUnitsPerUnit),
              toNumber(totalLapsedUnitsForExcessRevenuePerc),
              toNumber(transmissionChargesPerKWh),
              toNumber(wheelingCharges),
              toNumber(electricityDutyPerUnit),
              perKW,
              perKWh,
              toNumber(transmissionChargesPerKwPerMonth),
              toNumber(discountRate),
              includeDepreciation,
              toNumber(bankingChargeRs),
            );
            
            console.log(`Successfully edited operational costs node ${initialData.name}`);
          } else if (!isReconfiguration && inputNodes.extrapolation_input && inputNodes.loan_input && inputNodes.depreciation_input) {
            // We're creating a new node
            console.log(`Calling API to append operational costs node: ${nodeName} to pipeline: ${session.pipeline_name}`);
            console.log(`Extrapolation node: ${inputNodes.extrapolation_input}, Loan node: ${inputNodes.loan_input}, Depreciation node: ${inputNodes.depreciation_input}`);
            console.log(`Position: (${position.x}, ${position.y})`);
            
            await appendOperationalCostsNodeToPipeline(
              session.pipeline_name,
              nodeName,
              inputNodes.extrapolation_input,
              inputNodes.loan_input,
              inputNodes.depreciation_input,
              toNumber(noOfBopYear),
              toNumber(windBopEscalationPerc),
              toNumber(windEscalationPerc),
              toNumber(solarEscalationPerc),
              // Pass raw wind non-BOP maintenance so backend can accept dict / CSV
              annualMaintenancePerTurbineInLakhs,
              toNumber(annualMaintenancePerTurbineInLakhsWithBop),
              toNumber(annualMaintenancePerSolarUnitInLakhs),
              toNumber(insurancePercentOfAssetCost),
              toNumber(operationalCharges),
              toNumber(assetManagementFees),
              toNumber(annualLeaseChargePerAcreSolar),
              toNumber(annualLeaseChargePerAcreWind),
              toNumber(annualLeaseEscalationPerc),
              toNumber(solarAcresPerMWp),
              toNumber(windAcresPerWTG),
              firstYearBenifits,
              toNumber(revenueFromExcessBankedUnitsPerUnit),
              toNumber(totalLapsedUnitsForExcessRevenuePerc),
              toNumber(transmissionChargesPerKWh),
              toNumber(wheelingCharges),
              toNumber(electricityDutyPerUnit),
              perKW,
              perKWh,
              toNumber(transmissionChargesPerKwPerMonth),
              toNumber(discountRate),
              includeDepreciation,
              toNumber(bankingChargeRs),
              position.x,
              position.y,
            );
            
            console.log(`Successfully added operational costs node ${nodeName} to backend`);
          } else if (isReconfiguration) {
            console.log(`Skipping API call for node ${nodeName} as it's being reconfigured by parent component`);
          }
        } catch (apiError) {
          console.error(`Error calling ${isEditing ? 'editOperationalCostsNode' : 'appendOperationalCostsNodeToPipeline'}:`, apiError);
          const info = notifyError(apiError, {
            operation: isEditing ? OP.node.editOperationalCosts : OP.node.appendOperationalCosts,
            showToast: false,
          });
          setSubmitError(info);
          // Don't continue with UI updates if API call fails
          setIsSubmitting(false);
          return;
        }
      } else {
        console.log(`Skipping API call for unsaved pipeline`);
      }
      
      // Call the callback to update the UI
        onConfigured({
        name: isEditing ? initialData.name : nodeName, // Keep original name in edit mode
        no_of_bop_year: toNumber(noOfBopYear),
        wind_bop_escalation_perc: toNumber(windBopEscalationPerc),
        wind_escalation_perc: toNumber(windEscalationPerc),
        solar_escalation_perc: toNumber(solarEscalationPerc),
        discount_rate: toNumber(discountRate),
        // Keep raw value for wind non-BOP so node_func can parse dict/CSV
        annual_maintenance_per_turbine_in_lakhs: annualMaintenancePerTurbineInLakhs,
        annual_maintenance_per_turbine_in_lakhs_with_bop: toNumber(annualMaintenancePerTurbineInLakhsWithBop),
        annual_maintenance_per_solar_unit_in_lakhs: toNumber(annualMaintenancePerSolarUnitInLakhs),
        insurance_percent_of_asset_cost: toNumber(insurancePercentOfAssetCost),
        operational_charges: toNumber(operationalCharges),
        asset_management_fees: toNumber(assetManagementFees),
        annual_lease_charge_per_acre_solar: toNumber(annualLeaseChargePerAcreSolar),
        annual_lease_charge_per_acre_wind: toNumber(annualLeaseChargePerAcreWind),
        annual_lease_escalation_perc: toNumber(annualLeaseEscalationPerc),
        solar_acres_per_MWp: toNumber(solarAcresPerMWp),
        wind_acres_per_WTG: toNumber(windAcresPerWTG),
        first_year_benifits: firstYearBenifits,
        revenue_from_excess_banked_units_per_unit: toNumber(revenueFromExcessBankedUnitsPerUnit),
        total_lapsed_units_for_excess_revenue_perc: toNumber(totalLapsedUnitsForExcessRevenuePerc),
        transmission_charges_per_KWh: toNumber(transmissionChargesPerKWh),
        wheeling_charges: toNumber(wheelingCharges),
        electricity_duty_per_unit: toNumber(electricityDutyPerUnit),
        banking_charge_rs: toNumber(bankingChargeRs),
        include_depreciation: includeDepreciation,
        
      });
      
      // Reset submitting state
      setIsSubmitting(false);
      
      // Note: We don't close the modal here as it will be closed by the parent component
    } catch (error) {
      console.error('Error configuring operational costs node:', error);
      const info = notifyError(error, { showToast: false });
      setSubmitError(info);
      setIsSubmitting(false);
      return; // Exit early on error
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      style={customStyles}
      contentLabel="Operational Costs Configuration"
      shouldCloseOnOverlayClick={true}
      shouldCloseOnEsc={true}
      ariaHideApp={false} // Prevents warning about appElement
    >
      <div className="operational-costs-modal">
        <button onClick={onClose} className="operational-costs-modal-close">
          <IoCloseSharp size={24} />
        </button>
        
        <h2 className="operational-costs-modal-title">
          {isReconfiguration ? "Reconfigure Operational Costs Node" : 
           isEditing ? "Edit Operational Costs Parameters" : 
           "Configure New Operational Costs Node"}
        </h2>

        {submitError && (
          <div
            role="alert"
            className="operational-costs-modal-error"
            style={{
              marginTop: '0.75rem',
              marginBottom: '0.75rem',
              padding: '0.75rem',
              borderRadius: '0.375rem',
              background: 'rgba(239, 68, 68, 0.12)',
              border: '1px solid rgba(239, 68, 68, 0.35)',
              color: '#b91c1c',
              fontSize: '0.95rem',
            }}
          >
            <div style={{ fontWeight: 700, marginBottom: '0.25rem' }}>{submitError.title}</div>
            <div>{submitError.message}</div>
          </div>
        )}
        
        <div className="operational-costs-modal-field">
          <label className="operational-costs-modal-label">
            Node Name: <span className="required">*</span>
          </label>
          {isEditing ? (
            <input
              type="text"
              value={nodeName}
              className="operational-costs-modal-input-full"
              disabled={true}
              title="Node name cannot be changed in edit mode"
            />
          ) : (
            <input
              type="text"
              value={nodeName}
              onChange={(e) => setNodeName(e.target.value)}
              onBlur={() => {
                // Validate again on blur to ensure we show errors when user leaves the field
                const validation = validateNodeName(nodeName);
                setNodeNameError(validation.isValid ? '' : validation.errorMessage);
              }}
              className={`operational-costs-modal-input-full ${nodeNameError ? 'error' : ''}`}
              placeholder="Enter node name (letters, digits, hyphens, underscores, periods)"
              aria-invalid={!!nodeNameError}
              aria-describedby={nodeNameError ? "node-name-error" : undefined}
            />
          )}
          {!isEditing && nodeNameError && (
            <div className="operational-costs-modal-error" id="node-name-error" role="alert">{nodeNameError}</div>
          )}
          {!isEditing && !nodeNameError && (
            <div className="operational-costs-modal-hint">
              Valid characters: letters, digits, hyphens (-), underscores (_), periods (.)
            </div>
          )}
        </div>

        {/* Basic configuration handled in Setup Utility */}

        {/* Maintenance Costs - Excel-style table with rows: Wind, Wind BOP, Solar */}
        <div className="operational-costs-modal-section">
          <h3 className="operational-costs-modal-section-title">Operational Costs - Maintenance Costs</h3>
          <div className="operational-costs-table-wrapper">
            <table className="operational-costs-table">
              <thead>
                <tr>
                  <th>Particular</th>
                  <th>O & M charges in lakh</th>
                  <th>Annual Escalation in %</th>
                  <th>Free O&M period in Years</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Wind</td>
                  <td>
                    <input
                      type="text"
                      value={annualMaintenancePerTurbineInLakhs}
                      onChange={(e) => setAnnualMaintenancePerTurbineInLakhs(e.target.value)}
                      placeholder="e.g. 5 or 4, 5, 5, 6"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={windEscalationPerc}
                      onChange={(e) => setWindEscalationPerc(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={noOfBopYear}
                      onChange={(e) => setNoOfBopYear(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="1"
                    />
                  </td>
                </tr>
                <tr>
                  <td>Wind BOP</td>
                  <td>
                    <input
                      type="number"
                      value={annualMaintenancePerTurbineInLakhsWithBop}
                      onChange={(e) => setAnnualMaintenancePerTurbineInLakhsWithBop(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={windBopEscalationPerc}
                      onChange={(e) => setWindBopEscalationPerc(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input type="number" value={0} disabled />
                  </td>
                </tr>
                <tr>
                  <td>Solar</td>
                  <td>
                    <input
                      type="number"
                      value={annualMaintenancePerSolarUnitInLakhs}
                      onChange={(e) => setAnnualMaintenancePerSolarUnitInLakhs(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={solarEscalationPerc}
                      onChange={(e) => setSolarEscalationPerc(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input type="number" value={0} disabled />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Land Charges: Excel-style with fixed columns and two rows for Wind and Solar */}
        <div className="operational-costs-modal-section">
          <h3 className="operational-costs-modal-section-title">Operational Costs - Land Charges</h3>
          <div className="operational-costs-table-wrapper">
            <table className="operational-costs-table">
              <thead>
                <tr>
                  <th>Particular</th>
                  <th>Acres</th>
                  <th>Annual lease in Rs.</th>
                  <th>Annual Escalation %</th>
                  <th>Lease free period</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Wind / Turbine</td>
                  <td>
                    <input
                      type="number"
                      value={windAcresPerWTG}
                      onChange={(e) => setWindAcresPerWTG(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={annualLeaseChargePerAcreWind}
                      onChange={(e) => setAnnualLeaseChargePerAcreWind(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={annualLeaseEscalationPerc}
                      onChange={(e) => setAnnualLeaseEscalationPerc(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input type="text" value={0} disabled title="Not configured" />
                  </td>
                </tr>
                <tr>
                  <td>Solar</td>
                  <td>
                    <input
                      type="number"
                      value={solarAcresPerMWp}
                      onChange={(e) => setSolarAcresPerMWp(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={annualLeaseChargePerAcreSolar}
                      onChange={(e) => setAnnualLeaseChargePerAcreSolar(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={annualLeaseEscalationPerc}
                      onChange={(e) => setAnnualLeaseEscalationPerc(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input type="text" value={0} disabled title="Not configured" />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Operational Charges Section */}
        <div className="operational-costs-modal-section">
          <h3 className="operational-costs-modal-section-title">Operational Charges</h3>
          <div className="operational-costs-table-wrapper">
            <table className="operational-costs-table">
              <thead>
                <tr>
                  <th>Operational Charges (INR)</th>
                  <th>Asset Management Fees (INR)</th>
                  <th>{perKWh ? 'Transmission Charges per KWh (INR)' : (perKW ? 'Transmission Charges per kW per Month (INR)' : 'Transmission Charges')}</th>
                  <th>Wheeling Charges (INR)</th>
                  <th>Electricity Duty per Unit (INR)</th>
                  <th>Banking charge (Rs/unit) <span style={{ fontWeight: 400 }}>(GH_2025)</span></th>
                  <th>Insurance % of Asset Cost</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input
                      type="number"
                      value={operationalCharges}
                      onChange={(e) => setOperationalCharges(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={assetManagementFees}
                      onChange={(e) => setAssetManagementFees(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={perKWh ? transmissionChargesPerKWh : (perKW ? transmissionChargesPerKwPerMonth : 0)}
                      onChange={(e) => {
                        const val = e.target.value === '' ? '' : Number(e.target.value);
                        if (perKWh) {
                          setTransmissionChargesPerKWh(val);
                        } else if (perKW) {
                          setTransmissionChargesPerKwPerMonth(val);
                        }
                      }}
                      min="0"
                      step="0.01"
                      disabled={!perKWh && !perKW}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={wheelingCharges}
                      onChange={(e) => setWheelingCharges(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={electricityDutyPerUnit}
                      onChange={(e) => setElectricityDutyPerUnit(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={bankingChargeRs}
                      onChange={(e) => setBankingChargeRs(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                      placeholder="Optional"
                      title="Only used when Banking policy is GH_2025"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={insurancePercentOfAssetCost}
                      onChange={(e) => setInsurancePercentOfAssetCost(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <div className="operational-costs-table-wrapper" style={{ marginTop: 12 }}>
            <table className="operational-costs-table">
              <thead>
                <tr>
                  <th>Rate per unit for excess banked units (INR/unit)</th>
                  <th>Percent of lapsed units eligible for excess revenue (%)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>
                    <input
                      type="number"
                      value={revenueFromExcessBankedUnitsPerUnit}
                      onChange={(e) => setRevenueFromExcessBankedUnitsPerUnit(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      step="0.01"
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      value={totalLapsedUnitsForExcessRevenuePerc}
                      onChange={(e) => setTotalLapsedUnitsForExcessRevenuePerc(e.target.value === '' ? '' : Number(e.target.value))}
                      min="0"
                      max="100"
                      step="0.01"
                    />
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        {/* Additional Options Section */}
        <div className="operational-costs-modal-section">
          <h3 className="operational-costs-modal-section-title">Additional Options</h3>
          <div className="operational-costs-table-wrapper">
            <table className="operational-costs-table">
              <thead>
                <tr>
                  <th>First Year Benefits (Depreciation)</th>
                  <th>Charges per kW (per_KW)</th>
                  <th>Charges per kWh (per_KWh)</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={firstYearBenifits}
                      onChange={(e) => setFirstYearBenifits(e.target.checked)}
                      className="operational-costs-modal-checkbox"
                    />
                  </td>
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={perKW}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setPerKW(checked);
                        if (checked) {
                          setPerKWh(false);
                          setTransmissionChargesPerKWh(0);
                        }
                      }}
                      className="operational-costs-modal-checkbox"
                    />
                  </td>
                  <td className="checkbox-cell">
                    <input
                      type="checkbox"
                      checked={perKWh}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setPerKWh(checked);
                        if (checked) {
                          setPerKW(false);
                          setTransmissionChargesPerKwPerMonth(0);
                        }
                      }}
                      className="operational-costs-modal-checkbox"
                    />
                  </td>
                  <td className="checkbox-cell">
                    <label style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={includeDepreciation}
                        onChange={(e) => setIncludeDepreciation(e.target.checked)}
                        className="operational-costs-modal-checkbox"
                      />
                      <span>Include Depreciation in Total Cost</span>
                    </label>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
        
        <div className="operational-costs-modal-button-container">
          <button 
            className="operational-costs-modal-configure-button"
            onClick={handleConfigure}
            disabled={
              isSubmitting || 
              (!isEditing && (!!nodeNameError || !nodeName))
            }
            title={
              nodeNameError ? nodeNameError : 
              (!nodeName && !isEditing) ? 'Node name is required' : ''
            }
          >
            {isSubmitting ? 'Processing...' : 
              isReconfiguration ? 'Reconfigure' : 
              isEditing ? 'Save Changes' : 
              'Configure'}
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default OperationalCostsConfigModal;